import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import MatrixPage from '@/pages/matrix/MatrixPage';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

/**
 * GlobalMatrixPage - Loads all company projects and passes them to MatrixPage
 * as a stack view, giving a company-wide training matrix.
 */
export const GlobalMatrixPage = () => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);

  usePageTitle('Training Matrix');

  useEffect(() => {
    const loadProjects = async () => {
      if (!company?.id) return;

      try {
        setLoading(true);

        const { data, error: queryError } = await supabase
          .from('projects')
          .select('id, name')
          .eq('company_id', company.id)
          .eq('is_archived', false);

        if (queryError) throw queryError;

        setProjects(data || []);
      } catch (err) {
        console.error('Error loading company projects:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [company?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <MatrixPage
      stackId="global"
      stackName="Training Matrix"
      stackProjects={projects}
      isStackView={true}
    />
  );
};
